import dataUtils from 'data/utils';
import { toImmutable, Immutable } from 'nuclear-js';

import CommonValues from 'resources/common-values';
import ComparisonUtils from 'components/student-profile/modules/comparisons/utils.js';

import { ritQuintilePercentiles, MIN_RIT, MAX_RIT, StandardTypes, nweaGradeNames } from 'resources/common-values';

const buildDistrictAveragePayload = (studentTermData, subjects, currentDistrictAverages) => {
    const payload = [];
    const keyset = dataUtils.findMissingDistrictAverageKeys(studentTermData, subjects, currentDistrictAverages);

    // build up payload values for each term, subject, and quintile value
    keyset.forEach((k) => {
        payload.push({
            ...k,
            groupTypeDescription: 'NONE',
        });
    });

    return payload;
};

export const buildGrowthProjectionsPayload = (mostRecentTestEventBySubject, studentTermData) => {
    const payload = [];

    mostRecentTestEventBySubject.forEach((event, subject) => {
        const startTerm = studentTermData.find((t) => t.get('termNumber') === event.get('termNumber'));

        if (startTerm) {
            // can project up to 1 year in future
            studentTermData
                .filter(
                    (t) =>
                        t.get('termNumber') > startTerm.get('termNumber') &&
                        t.get('termNumber') <= startTerm.get('termNumber') + 100
                )
                .forEach((endTerm) => {
                    payload.push({
                        subject: event.get('measurementScaleID'), // The payload requires 'subject', but we want to add measurementScaleID to support Course Specific Test Events
                        startingTerm: startTerm.get('termNumber'),
                        startingWeek: startTerm.get('weeks'),
                        grade: startTerm.get('grade'),
                        startingRit: event.get('score'),
                        startingSem: event.get('standardError'),
                        endingTerm: endTerm.get('termNumber'),
                        endingWeek: endTerm.get('weeks'),
                    });
                });
        }
    });

    return payload;
};

export const buildNormDataForGrowthGoalPayload = (subject, endTermNumber, testEvent, studentTermData) => {
    if (!subject || !endTermNumber || !testEvent || !studentTermData) {
        throw new Error(
            `buildNormDataForGrowthGoalPayload: Unexpected condition: one or more arguments not set: ${arguments}`
        );
    }

    const startTerm = studentTermData.find((t) => t.get('termNumber') === testEvent.get('termNumber'));
    // this shouldn't happen as student should be rostered for all terms they have growth event
    if (!startTerm) {
        return null;
    }

    const endTerm = studentTermData.find((t) => t.get('termNumber') === endTermNumber);
    // this can happen if grade couldn't be calculated for the endTerm (e.g., student was 12th grade last year)
    if (!endTerm) {
        return null;
    }

    return {
        subject,
        startingRit: testEvent.get('score'),
        startingSem: testEvent.get('standardError'),
        startingSeason: testEvent.get('termNumber') % 10,
        startingGrade: startTerm.get('grade'),
        startingWeek: startTerm.get('instructionalWeek'),
        endingSeason: endTermNumber % 10,
        endingGrade: endTerm.get('grade'),
        endingWeek: endTerm.get('instructionalWeek'),
        cgpGoalStart: 1,
        cgpGoalEnd: 99,
        cgpGoalStep: 1,
        ritGoalStart: testEvent.get('score') + 1,
        ritGoalEnd: testEvent.get('score') + 50,
        ritGoalStep: 1,
        statusPercStart: 1,
        statusPercEnd: 99,
        statusPercStep: 1,
    };
};

export const buildNormDataForPerformanceGoalPayload = (subject, endTermNumber, studentTermData) => {
    if (!subject || !endTermNumber || !studentTermData) {
        throw new Error(
            `buildNormDataForPerformanceGoalPayload: Unexpected condition: one or more arguments not set: ${arguments}`
        );
    }

    const endTerm = studentTermData.find((t) => t.get('termNumber') === endTermNumber);
    // this can happen if grade couldn't be calculated for the endTerm (e.g., student was 12th grade last year)
    if (!endTerm) {
        return null;
    }

    return {
        subject,
        season: endTermNumber % 10,
        grade: endTerm.get('grade'),
        weeks: endTerm.get('instructionalWeek'),
        ritGoalStart: MIN_RIT,
        ritGoalEnd: MAX_RIT,
        ritGoalStep: 1,
        statusPercStart: 1,
        statusPercEnd: 99,
        statusPercStep: 1,
    };
};

export function buildRequestPayloadForLinkingStudies(measurementScaleID, firstOpenTermId, termInfo, linkingStudies) {
    const studies = linkingStudies ? linkingStudies.filter((ls) => !!ls) : [];

    let payload = [];

    termInfo
        .filter((t) => t.get('termNumber') >= firstOpenTermId && t.get('grade', false))
        .forEach((t) => {
            studies.forEach((s) => {
                const termNumber = t.get('termNumber');
                const season = termNumber % 100;
                payload.push({
                    termNumber: termNumber,
                    study: s.get('study'),
                    studyType: s.get('studyType'),
                    subject: measurementScaleID,
                    grade: t.get('grade'),
                    season: season,
                });
            });
        });

    return toImmutable(payload);
}

const buildRitQuintilePayload = (studentTermData, subjects, currentRitQuintiles) => {
    const payload = [];

    const keyset = dataUtils.findMissingRitQuintileKeys(currentRitQuintiles, studentTermData, subjects);

    // build up payload values for each term, subject, and quintile value
    keyset.forEach((k) => {
        ritQuintilePercentiles.forEach((p) => {
            payload.push({
                termNumber: k.termNumber,
                subject: k.subject,
                grade: k.grade,
                weeks: k.weeks,
                percentile: p,
            });
        });
    });

    return payload;
};

/**
 * Given a list of terms for a particular student and a list of test events this will construct a payload for the
 * norms service used to ask for growth norms for various comparison periods required within the comparisons module.
 * @param studentTermData The students term data
 * @param studentTestEvents the students test events
 * @returns {*} an array containing the payload to pass to the norms service requesting growth data.
 */
export function buildGrowthNormPercentilesPayload(studentTermData, studentTestEvents) {
    const conditionalPushItem = (toTerm, toTestEvent, fromTermNumber, payload, msid) => {
        const fromTerm = studentTermData.find((t) => t.get('termNumber') === fromTermNumber);
        const fromTestEvent = ComparisonUtils.getValidGrowthTestEventByMsid(studentTestEvents, fromTermNumber, msid);

        if (fromTerm && fromTestEvent) {
            payload.push({
                subject: msid,
                grade: toTerm.get('grade'),
                startingWeek: fromTerm.get('weeks'),
                endingWeek: toTerm.get('weeks'),
                startingSeason: fromTerm.get('termNumber') % 10,
                endingSeason: toTerm.get('termNumber') % 10,
                startingRit: fromTestEvent.get('score'),
                endingRit: toTestEvent.get('score'),
                startingSem: fromTestEvent.get('standardError'),
                endingSem: toTestEvent.get('standardError'),
            });
        }
    };

    if (studentTermData && studentTermData.size > 0 && studentTestEvents && studentTestEvents.size > 0) {
        let payload = [];
        /* The original logic for this function was to cycle through each subject and build a request for each of the
         * growth periods during the last 4 terms (including the term being analyzed - aka 'toTerm').
         * This function was updated to ensure that when building the payload request, we use the course ID (aka MSID).
         * When reviewing the code below, notice that we still use "subject.get('bid')" when we pull in the
         * 'toTestEvent'. That's because for a given term, we don't know what the relevant course ID is for the
         * "subject" tab. Therefore, we make this generic request for a valid growth test event for the indicated
         * term and subject. Once we have the 'toTerm', we know what its course ID is, so when we ask for the
         * 'fromTestEvent', we reference the MSID of the 'toTestEvent' in order to ensure that we request the correct
         * information from the API.
         */
        studentTermData.forEach((toTerm) => {
            const toTermNumber = toTerm.get('termNumber');
            CommonValues.subjects.forEach((subject) => {
                //See if we have a valid test event for each term
                let toTestEvent = ComparisonUtils.getValidGrowthTestEvent(
                    studentTestEvents,
                    toTermNumber,
                    subject.get('bid')
                );
                if (toTestEvent) {
                    const fromTermNumbers = ComparisonUtils.getGrowthComparisonFromTermNumbers(toTermNumber);
                    fromTermNumbers.forEach((fromTermNumber) => {
                        conditionalPushItem(
                            toTerm,
                            toTestEvent,
                            fromTermNumber,
                            payload,
                            toTestEvent.get('measurementScaleID')
                        );
                    });
                }
            });
        });
        return payload;
    }
    return null;
}

export default {
    buildGrowthNormPercentilesPayload,
    buildDistrictAveragePayload,
    buildGrowthProjectionsPayload,
    buildNormDataForGrowthGoalPayload,
    buildNormDataForPerformanceGoalPayload,
    buildRequestPayloadForLinkingStudies,
    buildRitQuintilePayload,
};
